import { Sparkles } from 'lucide-react'
import { SectionHeader } from '../primitives/SectionHeader'
import { type ClusterComposition, cssVarForType, ENTITY_TYPE_COLOR_ORDER } from '../selectors'

const LEGEND_TYPES: Array<{ label: string; cssVar: string }> = [
  { label: 'PhoneNumber', cssVar: 'var(--chart-1)' },
  { label: 'Person', cssVar: 'var(--chart-2)' },
  { label: 'Location', cssVar: 'var(--chart-3)' },
  { label: 'Vehicle / FuelStation', cssVar: 'var(--chart-4)' },
  { label: 'Other', cssVar: cssVarForType('__fallback__') },
]

export interface ClusterCompositionChartProps {
  data: ClusterComposition[]
  labelFor: (compId: number) => string
  onSelect?: (compId: number) => void
  onEmptyAction?: () => void
}

export function ClusterCompositionChart({
  data,
  labelFor,
  onSelect,
  onEmptyAction,
}: ClusterCompositionChartProps) {
  const sampleTotal = data.reduce((sum, c) => sum + c.sampleCount, 0)

  if (data.length === 0) {
    return (
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
        <SectionHeader
          title="Cluster composition"
          subLabel="Top clusters by size and type mix"
          guide={{
            to: '/docs/investigation-analysis#graph-clusters',
            hint: 'Clusters are detected with Louvain community detection over the case graph.',
          }}
        />
        <div className="rounded-lg border border-dashed border-[var(--border)] p-8 flex flex-col items-center text-center gap-2">
          <Sparkles className="size-6 text-[var(--muted-foreground)]" />
          <div className="text-sm font-medium text-[var(--foreground)]">No clusters yet</div>
          <p className="text-xs text-[var(--muted-foreground)] max-w-xs">
            Run Recompute to analyse the case graph and detect communities.
          </p>
          {onEmptyAction && (
            <button
              type="button"
              onClick={onEmptyAction}
              className="mt-1 h-7 px-2.5 rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] text-xs font-medium"
            >
              Run analysis
            </button>
          )}
        </div>
      </div>
    )
  }

  const max = Math.max(...data.map(r => r.size))

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
      <SectionHeader
        title="Cluster composition"
        subLabel={`Top ${data.length} cluster${data.length === 1 ? '' : 's'} by size · sample-entity type mix (N=${sampleTotal})`}
        guide={{
          to: '/docs/investigation-analysis#graph-clusters',
          hint: 'Bars show cluster size; segments show entity-type mix from sample entities.',
        }}
      />
      <div className="space-y-2.5 max-md:space-y-0.5">
        {data.map(row => {
          const widthPct = (row.size / max) * 100
          const label = labelFor(row.comp_id)
          return (
            <button
              type="button"
              key={row.comp_id}
              onClick={() => onSelect?.(row.comp_id)}
              className="grid grid-cols-[90px_1fr_32px] items-center gap-2 w-full text-left rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] max-md:py-1.5"
            >
              <span className="text-xs text-[var(--muted-foreground)] truncate">{label}</span>
              <div className="h-4 rounded-md bg-[var(--muted)]/30 relative overflow-hidden">
                <div
                  className="h-4 flex overflow-hidden rounded-md"
                  style={{ width: `${widthPct}%` }}
                >
                  {row.segments.map((seg, i) => (
                    <div
                      key={`${seg.type}-${i}`}
                      className="h-full"
                      style={{
                        width: `${seg.share * 100}%`,
                        background: seg.color,
                      }}
                      title={`${seg.type} — ${Math.round(seg.share * 100)}%`}
                    />
                  ))}
                </div>
              </div>
              <span className="text-xs font-semibold tabular-nums text-[var(--foreground)] text-right">
                {row.size}
              </span>
            </button>
          )
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-3 pt-3 border-t border-[var(--border)]">
        {LEGEND_TYPES.map(l => (
          <span
            key={l.label}
            className="inline-flex items-center gap-1.5 text-3xs text-[var(--muted-foreground)]"
          >
            <span className="inline-block size-2 rounded-full" style={{ background: l.cssVar }} />
            {l.label}
          </span>
        ))}
      </div>

      {/* Screen-reader backup table */}
      <table className="sr-only">
        <caption>Cluster composition: size and sample-entity type mix</caption>
        <thead>
          <tr>
            <th>Cluster</th>
            <th>Size</th>
            <th>Type</th>
            <th>Share</th>
          </tr>
        </thead>
        <tbody>
          {data.flatMap(row =>
            row.segments.map(seg => (
              <tr key={`${row.comp_id}-${seg.type}`}>
                <td>{labelFor(row.comp_id)}</td>
                <td>{row.size}</td>
                <td>{seg.type}</td>
                <td>{Math.round(seg.share * 100)}%</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

// Re-export ENTITY_TYPE_COLOR_ORDER so callers don't have to dig through selectors.
export { ENTITY_TYPE_COLOR_ORDER }
